import { useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Globe, Send, Loader2 } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { BRAND, SERVICIOS } from "../data/content";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const EMAILJS_READY = Boolean(SERVICE_ID && TEMPLATE_ID && PUBLIC_KEY);

const INITIAL = { nombre: "", email: "", telefono: "", tipo: "", mensaje: "" };

const CONTACT_ITEMS = [
  { icon: MapPin, label: "Dirección", value: BRAND.address, testId: "contact-address" },
  { icon: Phone, label: "Teléfono", value: BRAND.phone, href: `tel:+52${BRAND.phone.replace(/\s/g, "")}`, testId: "contact-phone" },
  { icon: Mail, label: "Email", value: BRAND.email, href: `mailto:${BRAND.email}`, testId: "contact-email" },
  { icon: Globe, label: "Página Web", value: BRAND.web, testId: "contact-web" },
];

const inputCls =
  "w-full border border-cafe-line bg-white px-4 py-3.5 text-sm text-cafe-ink placeholder:text-cafe-muted/50 outline-none transition-[border-color,box-shadow] duration-300 focus:border-cafe focus:ring-2 focus:ring-cafe/20";

export const Contacto = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      if (EMAILJS_READY) {
        await emailjs.send(
          SERVICE_ID,
          TEMPLATE_ID,
          {
            from_name: form.nombre,
            reply_to: form.email,
            phone: form.telefono,
            project_type: form.tipo,
            message: form.mensaje,
          },
          PUBLIC_KEY
        );
        toast.success("Mensaje enviado correctamente. Te contactaremos pronto.");
      } else {
        await new Promise((r) => setTimeout(r, 1200));
        toast.success(
          "Tu mensaje ha sido registrado. El envío por correo se activará al conectar las claves de EmailJS."
        );
      }
      setForm(INITIAL);
    } catch (err) {
      toast.error("No se pudo enviar el mensaje. Inténtalo de nuevo o escríbenos por correo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contacto-section"
      className="relative bg-white py-20 sm:py-28 lg:py-36"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-12 gap-12 px-4 sm:px-6 lg:px-8">
        <div className="col-span-12 lg:col-span-5">
          <SectionHead num="06" eyebrow="Hablemos" title="Contacto" testId="contacto-head" />
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-cafe-muted">
              Cuéntanos sobre tu proyecto. Nuestro equipo técnico evaluará tus
              requerimientos y te dará una respuesta con el rigor que nos distingue.
            </p>
          </Reveal>
          <div className="mt-10 space-y-2">
            {CONTACT_ITEMS.map((item, i) => (
              <Reveal key={item.label} delay={0.08 * i}>
                <div
                  data-testid={item.testId}
                  className="group flex items-start gap-4 border border-transparent p-4 transition-[border-color,background-color] duration-300 hover:border-cafe-line hover:bg-cream"
                >
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center border border-cafe-line bg-cream transition-colors duration-300 group-hover:border-cafe group-hover:bg-cafe">
                    <item.icon className="h-4 w-4 text-cafe-dark transition-colors duration-300 group-hover:text-white" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] font-semibold uppercase tracking-[0.28em] text-cafe">
                      {item.label}
                    </span>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="mt-1 block text-sm font-medium leading-relaxed text-cafe-dark transition-colors duration-300 hover:text-cafe"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <span className="mt-1 block text-sm font-medium leading-relaxed text-cafe-dark">
                        {item.value}
                      </span>
                    )}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <Reveal delay={0.2}>
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="relative border border-cafe-line bg-cream p-7 sm:p-10"
            >
              <div aria-hidden="true" className="absolute -right-3 -top-3 -z-10 h-full w-full border border-cafe" />
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-cafe-dark">
                    Nombre completo *
                  </span>
                  <input
                    data-testid="contact-input-nombre"
                    required
                    value={form.nombre}
                    onChange={set("nombre")}
                    placeholder="Tu nombre"
                    className={inputCls}
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-cafe-dark">
                    Correo electrónico *
                  </span>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="nombre@empresa.com"
                    className={inputCls}
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-cafe-dark">
                    Teléfono
                  </span>
                  <input
                    data-testid="contact-input-telefono"
                    type="tel"
                    value={form.telefono}
                    onChange={set("telefono")}
                    placeholder="33 0000 0000"
                    className={inputCls}
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-cafe-dark">
                    Tipo de proyecto
                  </span>
                  <select
                    data-testid="contact-select-tipo"
                    value={form.tipo}
                    onChange={set("tipo")}
                    className={`${inputCls} appearance-none`}
                  >
                    <option value="">Selecciona una opción</option>
                    {SERVICIOS.map((s) => (
                      <option key={s.title} value={s.title}>
                        {s.title}
                      </option>
                    ))}
                    <option value="Otro">Otro</option>
                  </select>
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-cafe-dark">
                    Mensaje *
                  </span>
                  <textarea
                    data-testid="contact-input-mensaje"
                    required
                    rows={5}
                    value={form.mensaje}
                    onChange={set("mensaje")}
                    placeholder="Describe brevemente tu proyecto, ubicación y tiempos estimados."
                    className={`${inputCls} resize-none`}
                  />
                </label>
              </div>
              <div className="mt-7 flex flex-wrap items-center gap-5">
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  className="group flex items-center gap-2 rounded-full bg-cafe-dark px-8 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-cafe-deep disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      Enviando
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                  ) : (
                    <>
                      Enviar mensaje
                      <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
